/**
 * Production Partners Page
 *
 * Displays configured production partners (Scalable Press, Apliiq, Dimona)
 * These are API-only partners that handle garment decoration and fulfillment.
 * Unlike manufacturing partners, they don't need portal access.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Button,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Assessment as TestIcon,
} from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';

const ProductionPartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testingPartner, setTestingPartner] = useState(null);

  useEffect(() => {
    fetchProductionPartners();
  }, []);

  const fetchProductionPartners = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch production partner configuration from backend
      const response = await axios.get('/api/partner-quotes/partners');

      if (response.data.success) {
        setPartners(response.data.partners);
      }
    } catch (err) {
      console.error('Error fetching production partners:', err);
      setError('Failed to load production partners configuration');
    } finally {
      setLoading(false);
    }
  };

  const testPartnerConnection = async (partnerId) => {
    setTestingPartner(partnerId);

    try {
      const response = await axios.post(`/api/partner-quotes/test-connection/${partnerId}`);

      if (response.data.success) {
        toast.success(`${response.data.partnerName} connection successful!`);
      } else {
        toast.error(`Connection test failed: ${response.data.message}`);
      }
    } catch (err) {
      console.error('Error testing partner connection:', err);
      toast.error(`Failed to test connection: ${err.response?.data?.message || err.message}`);
    } finally {
      setTestingPartner(null);
    }
  };

  const getIntegrationClassColor = (integrationClass) => {
    switch (integrationClass) {
      case 'A':
        return 'success';
      case 'B':
        return 'primary';
      case 'C':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getIntegrationClassDescription = (integrationClass) => {
    switch (integrationClass) {
      case 'A':
        return 'Premium partner with private label, mockups, and premium materials';
      case 'B':
        return 'Strong partner with reliable production and competitive pricing';
      case 'C':
        return 'Basic production capabilities';
      default:
        return 'Unknown integration class';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <div>
            <Typography variant="h4" gutterBottom>
              Production Partners
            </Typography>
            <Typography variant="body1" color="text.secondary">
              API-integrated production partners for garment decoration and fulfillment
            </Typography>
          </div>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProductionPartners}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Production Partners</strong> are external API services (Scalable Press, Apliiq, Dimona) that handle
          garment decoration and fulfillment. They integrate via API only and do not require portal access.
          Configuration is managed via environment variables.
        </Alert>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Partner Cards */}
      <Grid container spacing={3}>
        {partners.map((partner) => (
          <Grid item xs={12} md={6} lg={4} key={partner.partnerId}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                {/* Partner Name and Status */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {partner.partnerName}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {partner.enabled ? (
                        <Chip
                          icon={<ActiveIcon />}
                          label="Active"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<InactiveIcon />}
                          label="Disabled"
                          color="default"
                          size="small"
                        />
                      )}
                      <Tooltip title={getIntegrationClassDescription(partner.integrationClass)}>
                        <Chip
                          label={`Class ${partner.integrationClass}`}
                          color={getIntegrationClassColor(partner.integrationClass)}
                          size="small"
                        />
                      </Tooltip>
                    </Stack>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* API Configuration */}
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  API Configuration
                </Typography>
                <Stack spacing={1} mb={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      API URL:
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {partner.apiUrl || 'Not configured'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Authentication:
                    </Typography>
                    <Typography variant="body2">
                      {partner.apiKey || partner.appKey ? (
                        <Chip label="Configured" color="success" size="small" />
                      ) : (
                        <Chip label="Missing" color="error" size="small" />
                      )}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Capabilities */}
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Capabilities
                </Typography>
                <Grid container spacing={1} mb={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Lead Time
                    </Typography>
                    <Typography variant="body2">
                      {partner.capabilities?.leadTimeDays || 'N/A'} days
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Quantity
                    </Typography>
                    <Typography variant="body2">
                      {partner.capabilities?.minQuantity || 'N/A'} units
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Max Quantity
                    </Typography>
                    <Typography variant="body2">
                      {partner.capabilities?.maxQuantity?.toLocaleString() || 'N/A'} units
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Webhooks
                    </Typography>
                    <Typography variant="body2">
                      {partner.capabilities?.hasWebhooks ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Supported Methods */}
                <Typography variant="subtitle2" gutterBottom color="text.secondary">
                  Decoration Methods
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5} mb={2}>
                  {partner.capabilities?.supportedMethods?.map((method) => (
                    <Chip
                      key={method}
                      label={method.replace('_', ' ').toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Stack>

                {/* Special Features */}
                {(partner.capabilities?.supportsPrivateLabel ||
                  partner.capabilities?.supportsMockups ||
                  partner.capabilities?.supportsMultiRegion) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom color="text.secondary">
                      Special Features
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                      {partner.capabilities?.supportsPrivateLabel && (
                        <Chip label="Private Label" color="primary" size="small" />
                      )}
                      {partner.capabilities?.supportsMockups && (
                        <Chip label="Mockups" color="primary" size="small" />
                      )}
                      {partner.capabilities?.supportsMultiRegion && (
                        <Chip
                          label={`Multi-Region: ${partner.capabilities.regions?.join(', ').toUpperCase()}`}
                          color="primary"
                          size="small"
                        />
                      )}
                    </Stack>
                  </>
                )}

                {/* Actions */}
                <Box mt={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={testingPartner === partner.partnerId ? <CircularProgress size={16} /> : <TestIcon />}
                    onClick={() => testPartnerConnection(partner.partnerId)}
                    disabled={!partner.enabled || testingPartner === partner.partnerId}
                  >
                    {testingPartner === partner.partnerId ? 'Testing...' : 'Test Connection'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {partners.length === 0 && !loading && (
        <Alert severity="warning">
          No production partners configured. Check your environment variables (.env) to enable partners.
        </Alert>
      )}

      {/* Configuration Guide */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configuration Guide
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" paragraph>
          Production partners are configured via environment variables in your <code>.env</code> file.
          Each partner requires specific credentials to enable API access.
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Partner</strong></TableCell>
                <TableCell><strong>Environment Variables</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partners.map((partner) => (
                <TableRow key={partner.partnerId}>
                  <TableCell>{partner.partnerName}</TableCell>
                  <TableCell>
                    <Typography variant="caption" component="div">
                      <code>{partner.partnerId.toUpperCase()}_ENABLED=true</code>
                    </Typography>
                    <Typography variant="caption" component="div">
                      <code>{partner.partnerId.toUpperCase()}_API_URL</code>
                    </Typography>
                    <Typography variant="caption" component="div">
                      <code>
                        {partner.appKey
                          ? `${partner.partnerId.toUpperCase()}_APP_KEY`
                          : `${partner.partnerId.toUpperCase()}_API_KEY`}
                      </code>
                    </Typography>
                    {partner.sharedSecret && (
                      <Typography variant="caption" component="div">
                        <code>{partner.partnerId.toUpperCase()}_SHARED_SECRET</code>
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {partner.enabled ? (
                      <Chip label="Enabled" color="success" size="small" />
                    ) : (
                      <Chip label="Disabled" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>Note:</strong> After updating environment variables, restart the server for changes to take effect.
          See <code>PRODUCTION_PARTNERS_SETUP.md</code> for detailed configuration instructions.
        </Alert>
      </Paper>
    </Container>
  );
};

export default ProductionPartnersPage;
